import { useEffect, useRef } from 'react';

import {
  CHUNK_DWCM_SCROLL_COOLDOWN_MS,
  CHUNK_SCROLL_SETTLE_MS,
  TranscriptChunkMeta,
  estimateChunkHeight,
  viewportNeedsWindowCatchUp,
} from 'utils/transcriptChunk.util';
import { chunkPerfLog, transcriptChunkStore } from './TranscriptChunkStore';

type HeightFor = (meta: TranscriptChunkMeta) => number;

export const spacerHeightBefore = (
  index: TranscriptChunkMeta[],
  startOrdinal: number,
  heightFor: HeightFor,
): number => {
  let h = 0;
  for (let i = 0; i < startOrdinal && i < index.length; i++) {
    h += heightFor(index[i]);
  }
  return h;
};

export const spacerHeightAfter = (
  index: TranscriptChunkMeta[],
  endOrdinal: number,
  heightFor: HeightFor,
): number => {
  let h = 0;
  for (let i = endOrdinal + 1; i < index.length; i++) {
    h += heightFor(index[i]);
  }
  return h;
};

export type WindowChangeInfo = {
  prependInfo?: {
    scrollTopBefore: number;
    prependedOrdinal: number;
  };
  catchUp?: boolean;
};

type CatchUpOptions = {
  scrollRoot: HTMLElement;
  index: TranscriptChunkMeta[];
  heightFor: HeightFor;
  onWindowChange: (info: WindowChangeInfo) => void;
  source?: 'settle';
  /** Stress-scroll settle — bypass playback / programmatic-scroll guards. */
  force?: boolean;
  /** Use settle anchor scrollTop (avoids drift from pre-remount compensation). */
  scrollTopOverride?: number;
};

/**
 * Align the merged window with the viewport after scroll settles.
 * Never call mid-scroll — that remounts Slate and fights the scrollbar.
 */
export const catchUpWindowForViewport = ({
  scrollRoot,
  index,
  heightFor,
  onWindowChange,
  source = 'settle',
  force = false,
  scrollTopOverride,
}: CatchUpOptions): boolean => {
  if (transcriptChunkStore.isFindJumpInProgress()) return false;

  if (!force && transcriptChunkStore.isPlaybackSeekInProgress()) return false;

  if (!force && transcriptChunkStore.isProgrammaticScroll()) return false;

  if (!force && !transcriptChunkStore.isEditorReady()) return false;

  const video = (window as any)?.Video as HTMLMediaElement | undefined;
  if (!force && video && !video.paused && !video.ended) return false;

  const activeWindow = transcriptChunkStore.getActiveWindow();
  if (!activeWindow) return false;

  const scrollTop = scrollTopOverride ?? scrollRoot.scrollTop;
  const viewportHeight = scrollRoot.clientHeight;

  const needsUpdate =
    force ||
    viewportNeedsWindowCatchUp(
      index,
      activeWindow,
      scrollTop,
      viewportHeight,
      heightFor,
    );

  if (!needsUpdate) return false;

  const caught = transcriptChunkStore.ensureWindowCoversViewport(
    scrollTop,
    viewportHeight,
    heightFor,
  );
  if (!caught) return false;

  chunkPerfLog('window:catch-up', {
    source,
    force,
    scrollTop: Number(scrollTop.toFixed(1)),
    window: transcriptChunkStore.getActiveWindow(),
  });

  if (!force) {
    transcriptChunkStore.markProgrammaticScroll(CHUNK_DWCM_SCROLL_COOLDOWN_MS);
  }
  onWindowChange({ catchUp: true });
  return true;
};

type SettleHookOptions = {
  scrollRoot: HTMLElement | null;
  index: TranscriptChunkMeta[];
  heightFor: HeightFor;
  enabled: boolean;
  onWindowChange: (info: WindowChangeInfo) => void;
  /** When true, catch-up is deferred until scroll settles (stress-scroll mode). */
  settleOnly?: boolean;
};

/**
 * SPP-675-2 — optional settle-only hook. Prefer catchUpWindowForViewport from
 * the shell's unified scroll handler when coordinating scroll-freeze UI.
 */
export const useBidirectionalScrollWindow = ({
  scrollRoot,
  index,
  heightFor,
  enabled,
  onWindowChange,
  settleOnly = true,
}: SettleHookOptions) => {
  const settleTimerRef = useRef(0);
  const onWindowChangeRef = useRef(onWindowChange);
  onWindowChangeRef.current = onWindowChange;

  useEffect(() => {
    if (!enabled || !scrollRoot || index.length <= 1) return;

    const onScroll = () => {
      if (!settleOnly) {
        catchUpWindowForViewport({
          scrollRoot,
          index,
          heightFor,
          onWindowChange: onWindowChangeRef.current,
          source: 'settle',
        });
      }

      if (settleTimerRef.current) {
        window.clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = window.setTimeout(() => {
        settleTimerRef.current = 0;
        catchUpWindowForViewport({
          scrollRoot,
          index,
          heightFor,
          onWindowChange: onWindowChangeRef.current,
          source: 'settle',
        });
      }, CHUNK_SCROLL_SETTLE_MS);
    };

    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollRoot.removeEventListener('scroll', onScroll);
      if (settleTimerRef.current) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, [enabled, scrollRoot, index, heightFor, settleOnly]);

  return {};
};

/** Default row height estimate for unseen chunks. */
export const defaultHeightFor = (meta: TranscriptChunkMeta) =>
  estimateChunkHeight(meta.blockCount);
